import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  BehaviorSubject,
  combineLatest,
  delay,
  filter,
  firstValueFrom,
  interval,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
  takeWhile
} from 'rxjs';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { NotificationService } from 'src/app/data/notification.service';
import { SignalRService } from 'src/app/data/signalr.service';
import { NotificationDto, PagedNotificationResult } from 'src/app/models/dtos/notification-dto';
import {
  NotificationModalComponent,
  NotificationModalComponentData,
  NotificationModalResult
} from '../../notification-modal/notification-modal.component';
import { NotificationUrgencyEnum } from 'src/app/models/enums/notification-urgency-enum';
import { SubSink } from 'SubSink';

type SwipeState = {
  pointerId: number;
  startX: number;
  startY: number;
  startT: number;
  lastX: number;
  swiping: boolean;
  moved: boolean;
};

@Component({
  selector: 'app-navbar-notifications',
  templateUrl: './navbar-notifications.component.html',
  styleUrls: ['./navbar-notifications.component.scss'],
  standalone: false
})
export class NavbarNotificationsComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private signalRService = inject(SignalRService);
  private dialog = inject(MatDialog);
  private notificationService = inject(NotificationService);

  private readonly pageSize = 10;
  private readonly hubName: string = 'NotificationsHub';

  private readonly reconnectCancelation$ = new Subject<void>();
  private readonly retryConnectionDelayMs: number = 5000;
  private readonly retryConnectionCount: number = 120;

  private readonly ringBellDurationMs: number = 4000;

  private readonly subs = new SubSink();

  private serverRevision = 0;
  private workerId: number | undefined;
  private notificationsRecieverSubscription: Subscription | undefined;

  private notificationsSubject = new BehaviorSubject<NotificationDto[]>([]);
  private allCountSubject = new BehaviorSubject<number>(0);
  private unreadNotificationsCountSubject = new BehaviorSubject<number>(0);
  private ringSubject = new Subject<void>();

  public notifications$ = this.notificationsSubject.asObservable();
  public allCount$ = this.allCountSubject.asObservable();
  public unreadNotificationsCount$ = this.unreadNotificationsCountSubject.asObservable();
  public hasNewNotifications$ = this.unreadNotificationsCount$.pipe(map(count => count > 0));
  public ring$ = this.ringSubject.asObservable().pipe(
    switchMap(() => of(false).pipe(delay(this.ringBellDurationMs), startWith(true)))
  );
  public loadedCount$ = this.notifications$.pipe(map(notifications => notifications.length));
  public showLoadMore$ = combineLatest([this.loadedCount$, this.allCount$]).pipe(
    map(([loadedCount, allCount]) => loadedCount < allCount)
  );

  public get notifications(): NotificationDto[] {
    return this.notificationsSubject.value;
  }

  private readonly listItemWidthFallbackPx: number = 280;

  private readonly swipeMinStartPx: number = 8;
  private readonly swipeVerticalCancelMultiplier: number = 1.2;

  private readonly swipeThresholdRatio: number = 0.75;
  private readonly swipeQuickRatio: number = 0.5;
  private readonly swipeQuickVelocityPxPerMs: number = 1.3;

  private readonly swipeExitExtraPx: number = 40;

  private readonly suppressClickMs: number = 350;

  private readonly swipeBgAlphaMin: number = 0.06;
  private readonly swipeBgAlphaMax: number = 0.18;
  private readonly swipeBgAlphaMaxClamp: number = 0.30;
  private readonly swipeBgAlphaFullProgressRatio: number = 0.75;

  private readonly snapbackTransitionRemoveDelayMs: number = 280;

  private swipeStates = new Map<number, SwipeState>();
  private suppressClickUntil = new Map<number, number>();

  async ngOnInit(): Promise<void> {
    await firstValueFrom(this.authService.authNavStatus$.pipe(filter((isAuthenticated) => isAuthenticated), take(1)));

    await this.fetchNextNotificationsPage();

    try {
      this.workerId = await firstValueFrom(this.authService.getWorkerIdByAuthServerUserId());
    } catch {
      // Not every user has a linked Worker record (e.g. HR/back-office staff).
      // SignalR routing is done server-side by AuthServerUserId, so the connection
      // must still be started even when there is no workerId.
      this.workerId = undefined;
    }

    this.startSignalRConnection();
  }

  ngOnDestroy(): void {
    if (this.notificationsRecieverSubscription) {
      this.notificationsRecieverSubscription.unsubscribe();
      this.notificationsRecieverSubscription = undefined;
    }

    this.reconnectCancelation$.next();
    this.reconnectCancelation$.unsubscribe();

    this.subs.unsubscribe();
  }

  private startSignalRConnection(): Observable<void> {
    if (this.notificationsRecieverSubscription) {
      this.notificationsRecieverSubscription.unsubscribe();
      this.notificationsRecieverSubscription = undefined;
    }

    const connected$ = this.signalRService
      .startConnection(this.hubName, this.workerId?.toString(), { onclose: async () => await this.reconnect() })
      .pipe(shareReplay());

    this.notificationsRecieverSubscription = connected$
      .pipe(switchMap(() => this.signalRService.receiveNotifications()))
      .subscribe((page: PagedNotificationResult) => this.store(page));

    return connected$;
  }

  private async fetchNextNotificationsPage(): Promise<void> {
    const page = await firstValueFrom(
      this.notificationService.getNotificationsPaged(this.notifications.length, this.pageSize)
    );
    this.store(page);
  }

  private store(page: PagedNotificationResult): void {
    this.serverRevision++;

    this.ringBell(page.Results);

    const merged = this.merge<NotificationDto>(
      this.notifications,
      page.Results,
      (left, right) => left.Id === right.Id
    );

    this.notificationsSubject.next(merged);
    this.allCountSubject.next(page.Count);
    this.unreadNotificationsCountSubject.next(page.UnreadCount);

    void this.openModalNotifications();
  }

  private ringBell(notifications: NotificationDto[]): void {
    const newestCurrent = this.notifications.find(() => true);
    if (notifications.some(n => !n.IsRead) && (!newestCurrent || notifications.some(n => n.Date > newestCurrent.Date))) {
      this.ringSubject.next();
    }
  }

  private merge<T>(left: T[], right: T[], compareFunc: (left: T, right: T) => boolean): T[] {
    const tmpRight = [...right];

    let result = left.reduce((acc, item) => {
      const index = tmpRight.findIndex(c => compareFunc(c, item));
      if (index > -1) {
        tmpRight.splice(index, 1);
      }

      acc.unshift(item);
      return acc;
    }, [] as any[]);

    result = result.concat(tmpRight);

    return (result as any[]).sort((a, b) => {
      return <any>new Date(b.Date) - <any>new Date(a.Date) || b.Id - a.Id;
    }) as T[];
  }

  public async loadMore(event: MouseEvent): Promise<void> {
    event.stopPropagation();
    await this.fetchNextNotificationsPage();
  }

  public async openModal(notification: NotificationDto): Promise<void> {
    const dialogRef = this.dialog.open<
      NotificationModalComponent,
      NotificationModalComponentData,
      NotificationModalResult
    >(NotificationModalComponent, {
      panelClass: 'details-dialog',
      data: { notification }
    });

    dialogRef.afterOpened().pipe(take(1)).subscribe(() => {
      void this.setAsRead(notification);
    });

    const result = await firstValueFrom(dialogRef.afterClosed());

    if (result?.deleted) {
      await this.deleteNotification(notification);
    }
  }

  async setAsRead(notification: NotificationDto): Promise<void> {
    if (notification.IsRead) {
      return;
    }

    const id = notification.Id;

    const prevNotifications = [...this.notifications];
    const prevUnreadCount = this.unreadNotificationsCountSubject.value;

    const index = prevNotifications.findIndex(n => n.Id === id);
    if (index === -1) {
      return;
    }

    const nextNotifications = [...prevNotifications];
    nextNotifications[index] = { ...nextNotifications[index], IsRead: true };

    await this.optimisticUpdate(
      () => {
        this.setNotifications(nextNotifications);
        this.setUnreadCount(prevUnreadCount - 1);
      },
      async () => {
        await firstValueFrom(this.notificationService.setAsRead(id));
      },
      () => {
        this.setNotifications(prevNotifications);
        this.setUnreadCount(prevUnreadCount);
      },
      () => {
        const now = [...this.notifications];
        const nowIndex = now.findIndex(n => n.Id === id);

        if (nowIndex > -1 && now[nowIndex].IsRead) {
          now[nowIndex] = { ...now[nowIndex], IsRead: false };
          this.setNotifications(now);
        }
      }
    );
  }

  async deleteNotification(notification: NotificationDto): Promise<void> {
    const id = notification.Id;

    const prevNotifications = [...this.notifications];
    const prevAllCount = this.allCountSubject.value;
    const prevUnreadCount = this.unreadNotificationsCountSubject.value;

    const existing = prevNotifications.find(n => n.Id === id);
    if (!existing) {
      return;
    }

    const wasUnread = !existing.IsRead;
    const nextNotifications = prevNotifications.filter(n => n.Id !== id);

    await this.optimisticUpdate(
      () => {
        this.setNotifications(nextNotifications);
        this.setAllCount(prevAllCount - 1);

        if (wasUnread) {
          this.setUnreadCount(prevUnreadCount - 1);
        }
      },
      async () => {
        await firstValueFrom(this.notificationService.delete(id));
      },
      () => {
        this.setNotifications(prevNotifications);
        this.setAllCount(prevAllCount);
        this.setUnreadCount(prevUnreadCount);
      },
      () => {
        const now = this.notifications;
        const stillMissing = !now.some(n => n.Id === id);

        if (stillMissing) {
          this.setNotifications([existing, ...now]);
        }
      }
    );
  }

  private clampNonNegative(value: number): number {
    return Math.max(0, value);
  }

  private setUnreadCount(value: number): void {
    this.unreadNotificationsCountSubject.next(this.clampNonNegative(value));
  }

  private setAllCount(value: number): void {
    this.allCountSubject.next(this.clampNonNegative(value));
  }

  private setNotifications(list: NotificationDto[]): void {
    this.notificationsSubject.next(list);
  }

  private async optimisticUpdate(
    applyOptimistic: () => void,
    request: () => Promise<void>,
    rollbackFull: () => void,
    rollbackPartial: () => void
  ): Promise<void> {
    const revAtStart = this.serverRevision;

    applyOptimistic();

    try {
      await request();
    } catch (e) {
      if (this.serverRevision === revAtStart) {
        rollbackFull();
      } else {
        rollbackPartial();
      }
      throw e;
    }
  }

  private async reconnect(): Promise<void> {
    await firstValueFrom(
      interval(this.retryConnectionDelayMs)
        .pipe(startWith(0))
        .pipe(map(() => this.startSignalRConnection()))
        .pipe(takeWhile(() => !this.signalRService.isConnected(this.hubName)))
        .pipe(takeUntil(this.reconnectCancelation$))
        .pipe(take(this.retryConnectionCount))
    );
  }

  private async openModalNotifications(): Promise<void> {
    for (const notification of this.notifications) {
      if (notification.NotificationUrgencyId === NotificationUrgencyEnum.Modal && !notification.IsRead) {
        await this.openModal(notification);
      }
    }
  }

  private getRowEl(target: EventTarget | null): HTMLElement | null {
    const el = target as HTMLElement | null;
    return el ? (el.closest('.swipe-row') as HTMLElement | null) : null;
  }

  private getFrontEl(target: EventTarget | null): HTMLElement | null {
    const el = target as HTMLElement | null;
    return el ? (el.closest('.swipe-row__front') as HTMLElement | null) : null;
  }

  private getBgEl(row: HTMLElement): HTMLElement | null {
    return row.querySelector<HTMLElement>('.swipe-row__bg');
  }

  private setSwipeBg(row: HTMLElement, progress: number, alpha: number): void {
    const bg = this.getBgEl(row);
    if (!bg) {
      return;
    }

    const p = Math.max(0, Math.min(progress, 1));
    const a = Math.max(0, Math.min(alpha, this.swipeBgAlphaMaxClamp));

    bg.style.setProperty('--swipe-progress', p.toFixed(3));
    bg.style.setProperty('--swipe-bg-alpha', a.toFixed(3));
  }

  private resetSwipeBg(row: HTMLElement): void {
    this.setSwipeBg(row, 0, 0);
  }

  private suppressClick(id: number, ms: number = this.suppressClickMs): void {
    this.suppressClickUntil.set(id, Date.now() + ms);
  }

  private isClickSuppressed(id: number): boolean {
    const until = this.suppressClickUntil.get(id);
    if (!until) {
      return false;
    }

    if (Date.now() > until) {
      this.suppressClickUntil.delete(id);
      return false;
    }

    return true;
  }

  onRowClick(ev: MouseEvent, n: NotificationDto): void {
    if (this.isClickSuppressed(n.Id)) {
      ev.preventDefault();
      ev.stopPropagation();
      return;
    }

    void this.openModal(n);
  }

  swipeStart(ev: PointerEvent, n: NotificationDto): void {
    if (ev.pointerType === 'mouse' && ev.button !== 0) {
      return;
    }

    const front = this.getFrontEl(ev.target);
    const row = this.getRowEl(ev.target);

    if (!front || !row) {
      return;
    }

    front.setPointerCapture(ev.pointerId);

    row.classList.remove('swipe-row--snapback');
    row.classList.remove('swipe-row--removing');
    row.classList.add('swipe-row--dragging');

    this.resetSwipeBg(row);

    this.swipeStates.set(n.Id, {
      pointerId: ev.pointerId,
      startX: ev.clientX,
      startY: ev.clientY,
      startT: performance.now(),
      lastX: 0,
      swiping: false,
      moved: false
    });
  }

  swipeMove(ev: PointerEvent, n: NotificationDto): void {
    const st = this.swipeStates.get(n.Id);
    if (!st || st.pointerId !== ev.pointerId) {
      return;
    }

    const front = this.getFrontEl(ev.target);
    const row = this.getRowEl(ev.target);

    if (!front || !row) {
      return;
    }

    const dx = ev.clientX - st.startX;
    const dy = ev.clientY - st.startY;

    if (!st.swiping) {
      if (Math.abs(dx) < this.swipeMinStartPx) {
        return;
      }

      if (Math.abs(dy) > Math.abs(dx) * this.swipeVerticalCancelMultiplier) {
        this.swipeCancel(n);
        return;
      }

      st.swiping = true;
    }

    st.moved = true;

    const w = front.offsetWidth || this.listItemWidthFallbackPx;
    const clamped = Math.max(0, Math.min(dx, w));
    st.lastX = clamped;

    const progressDenom = Math.max(1, w * this.swipeBgAlphaFullProgressRatio);
    const progress = Math.min(1, clamped / progressDenom);

    const alpha = this.swipeBgAlphaMin + progress * (this.swipeBgAlphaMax - this.swipeBgAlphaMin);
    this.setSwipeBg(row, progress, alpha);

    front.style.transition = 'none';
    front.style.transform = `translateX(${clamped}px)`;

    this.swipeStates.set(n.Id, st);

    this.suppressClick(n.Id);
  }

  async swipeEnd(ev: PointerEvent, n: NotificationDto): Promise<void> {
    const st = this.swipeStates.get(n.Id);
    if (!st || st.pointerId !== ev.pointerId) {
      return;
    }

    const front = this.getFrontEl(ev.target);
    const row = this.getRowEl(ev.target);

    this.swipeStates.delete(n.Id);

    if (!front || !row) {
      return;
    }

    if (st.swiping) {
      this.suppressClick(n.Id);
    }

    try {
      front.releasePointerCapture(ev.pointerId);
    } catch { }

    row.classList.remove('swipe-row--dragging');

    if (!st.swiping) {
      row.classList.add('swipe-row--snapback');
      this.resetSwipeBg(row);
      return;
    }

    const w = front.offsetWidth || this.listItemWidthFallbackPx;
    const dx = st.lastX;
    const dt = Math.max(1, performance.now() - st.startT);
    const velocity = dx / dt;

    const thresholdDistance = Math.floor(w * this.swipeThresholdRatio);
    const quickDistance = Math.floor(w * this.swipeQuickRatio);
    const decisive = dx >= thresholdDistance || (dx >= quickDistance && velocity >= this.swipeQuickVelocityPxPerMs);

    front.style.transition = '';

    if (!decisive) {
      row.classList.add('swipe-row--snapback');
      front.style.transform = 'translateX(0px)';
      this.resetSwipeBg(row);

      setTimeout(() => {
        row.classList.remove('swipe-row--snapback');
      }, this.snapbackTransitionRemoveDelayMs);

      return;
    }

    row.classList.remove('swipe-row--snapback');
    row.classList.add('swipe-row--removing');
    front.style.transform = `translateX(${w + this.swipeExitExtraPx}px)`;
    this.resetSwipeBg(row);

    await this.deleteNotification(n);
  }

  swipeCancel(n: NotificationDto): void {
    this.swipeStates.delete(n.Id);

    const front = document.querySelector<HTMLElement>(`.swipe-row__front[data-id="${n.Id}"]`);
    const row = front?.closest<HTMLElement>('.swipe-row');

    if (front) {
      front.style.transition = '';
      front.style.transform = 'translateX(0px)';
    }

    if (row) {
      row.classList.remove('swipe-row--dragging');
      row.classList.add('swipe-row--snapback');
      this.resetSwipeBg(row);

      setTimeout(() => {
        row.classList.remove('swipe-row--snapback');
      }, this.snapbackTransitionRemoveDelayMs);
    }

    this.suppressClick(n.Id);
  }
}
